import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getExchangeRateDetails, getCurrencies, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';

const EditExchangeRate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { currencies, isLoading, error, success } = useSelector((state) => state.auth);
  const [form, setForm] = useState({
    currency_id: '',
    date: '',
    rate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  // Responsive form width based on sidebar state
  useEffect(() => {
    const updateFormWidth = () => {
      const root = document.documentElement;
      const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]');

      if (sidebar) {
        const isCollapsed = sidebar.classList.contains('collapsed') ||
          sidebar.getAttribute('data-collapsed') === 'true' ||
          window.getComputedStyle(sidebar).width === '0px' ||
          sidebar.offsetWidth < 100;

        if (isCollapsed) {
          root.style.setProperty('--form-max-width', '100%');
        } else {
          root.style.setProperty('--form-max-width', '750px');
        }
      } else {
        const body = document.body;
        const hasSidebar = body.classList.contains('sidebar-open') ||
          body.getAttribute('data-sidebar-open') === 'true';

        if (!hasSidebar) {
          root.style.setProperty('--form-max-width', '100%');
        } else {
          root.style.setProperty('--form-max-width', '750px');
        }
      }
    };

    updateFormWidth();

    const observer = new MutationObserver(updateFormWidth);
    const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]') || document.body;

    if (sidebar) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class', 'data-collapsed'],
        childList: false,
        subtree: false
      });
    }

    window.addEventListener('resize', updateFormWidth);
    window.addEventListener('sidebar-toggle', updateFormWidth);
    document.addEventListener('sidebar-collapsed', updateFormWidth);
    document.addEventListener('sidebar-expanded', updateFormWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFormWidth);
      window.removeEventListener('sidebar-toggle', updateFormWidth);
      document.removeEventListener('sidebar-collapsed', updateFormWidth);
      document.removeEventListener('sidebar-expanded', updateFormWidth);
    };
  }, []);

  useEffect(() => {
    setTitle('exchangeRates.edit');
  }, [setTitle]);

  useEffect(() => {
    dispatch(getCurrencies({ page: 1, per_page: 100 }));
  }, [dispatch]);

  // Fetch exchange rate details
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoadingDetails(true);
        const token = getToken();
        const currentLang = getCurrentLanguage();
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${BASE_URL}/exchange-rates/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': currentLang
          },
        });

        if (response.data?.data) {
          const data = response.data.data;
          setForm({
            currency_id: data.currency_id ? String(data.currency_id) : '',
            date: data.date || '',
            rate: data.rate || ''
          });
        }
      } catch (err) {
        toast.error(t('exchangeRates.edit.loadError', { defaultValue: 'Failed to load exchange rate details' }), {
          rtl: isRTL,
          position: 'top-right',
          autoClose: 5000,
        });
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (id) {
      fetchDetails();
    }
  }, [id, t, isRTL]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success || t('exchangeRates.edit.updateSuccess', { defaultValue: 'Exchange rate updated successfully!' }), {
          rtl: isRTL,
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/exchange-rates');
      }, 1500);
    }
  }, [success, dispatch, navigate, isRTL, t]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'rate' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const submitData = {
        rate: parseFloat(form.rate)
      };

      // Only include date if it's provided
      if (form.date) {
        submitData.date = form.date;
      }

      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error('No authentication token found');

      const response = await axios.put(`${BASE_URL}/exchange-rates/${id}`, submitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang
        },
      });

      if (response.data?.success || response.status === 200) {
        toast.success(t('exchangeRates.edit.updateSuccess', { defaultValue: 'Exchange rate updated successfully!' }), {
          rtl: isRTL,
          position: 'top-right',
          autoClose: 3000,
        });
        setTimeout(() => navigate('/exchange-rates'), 1500);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || t('exchangeRates.edit.updateError', { defaultValue: 'Failed to update exchange rate' });
      toast.error(errorMessage, {
        rtl: isRTL,
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isWide = document.body.classList.contains('with-sidenav') || document.body.classList.contains('full-width');

  if (isLoadingDetails) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        direction: dir
      }}>
        <p>{t('common.loading', { defaultValue: 'Loading...' })}</p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      direction: dir
    }}>
      <div style={{ marginBottom: '10px', width: '100%', maxWidth: 'var(--form-max-width, 750px)' }}>
        <button
          onClick={() => navigate('/exchange-rates')}
          style={{
            background: '#666',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '10px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
          {t('common.back', { defaultValue: 'Back' })}
        </button>
      </div>
      <h1 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '14px',
        textAlign
      }}>
        {t('exchangeRates.edit.title', { defaultValue: 'Edit Exchange Rate' })}
      </h1>

      <form onSubmit={handleSubmit} style={{
        backgroundColor: '#1b3350',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: 'var(--form-max-width, 750px)',
        direction: dir
      }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('exchangeRates.form.date', { defaultValue: 'Date' })}
          </label>
          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              direction: dir
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('exchangeRates.form.rate', { defaultValue: 'Rate' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="number"

            name="rate"
            value={form.rate}
            onChange={handleChange}
            required
            placeholder={t('exchangeRates.form.ratePlaceholder', { defaultValue: 'Enter exchange rate' })}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              direction: dir,
              textAlign
            }}
          />
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginTop: '30px',
          flexDirection: isRTL ? 'row' : 'row-reverse'
        }}>
          <button
            type="button"
            onClick={() => navigate('/exchange-rates')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '12px 24px',
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1
            }}
          >
            {isSubmitting ? t('common.updating', { defaultValue: 'Updating...' }) : t('common.update', { defaultValue: 'Update' })}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditExchangeRate;
